import { PocketRow } from "@/components/ui";
/**
 * Task schedule section — displays and edits schedule info for a task.
 * Used inside the task detail panel.
 */
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { updateTask, pauseSchedule, resumeSchedule } from "@/api/tasks";
import type {
  Task,
  TaskSchedule,
  TaskScheduleInput,
  Recurrence,
  ScheduleType,
} from "@/api/tasks";
import { useTasksStore } from "@/stores/tasks-store";
import { formatNextRun, scheduleLabel } from "@/lib/schedule-format";
import { DatePickerDropdown } from "@/components/panels/date-picker-dropdown";
import type { RepeatOption } from "@/components/panels/repeat-options";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { panelsTaskScheduleMessages } from "@/i18n/messages/panels-task-schedule";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useInsideModalSurface } from "@/components/ui/modal-surface-context";

interface ScheduleFormState {
  scheduleType: ScheduleType;
  scheduledAt: string;
  rrule: Recurrence;
  rruleTime: string;
  rruleDay: number;
  timezone: string;
}

function defaultFormState(): ScheduleFormState {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return {
    scheduleType: "once",
    scheduledAt: "",
    rrule: "daily",
    rruleTime: "09:00",
    rruleDay: 0,
    timezone: tz,
  };
}

function formFromSchedule(s: TaskSchedule): ScheduleFormState {
  return {
    scheduleType: s.schedule_type,
    scheduledAt: s.scheduled_at ? s.scheduled_at.slice(0, 16) : "",
    rrule: (s.rrule as Recurrence) || "daily",
    rruleTime: s.rrule_time?.slice(0, 5) ?? "09:00",
    rruleDay: s.rrule_day ?? 0,
    timezone: s.timezone,
  };
}

/* ---------- Main component ---------- */

interface TaskScheduleSectionProps {
  projectId: string;
  task: Task;
}

export function TaskScheduleSection({
  projectId,
  task,
}: TaskScheduleSectionProps) {
  const t = useT(panelsTaskScheduleMessages);
  const tc = useT(commonMessages);
  const insideModal = useInsideModalSurface();
  const schedule = task.schedule;
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ScheduleFormState>(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshTask = useCallback((updated: Task) => {
    useTasksStore.setState((s) => ({
      tasks: s.tasks.map((t) => (t.id === updated.id ? updated : t)),
    }));
  }, []);

  const handleOpenForm = useCallback(() => {
    if (schedule) {
      setForm(formFromSchedule(schedule));
    } else {
      setForm(defaultFormState());
    }
    setShowForm(true);
  }, [schedule]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const input: TaskScheduleInput = {
        schedule_type: form.scheduleType,
        timezone: form.timezone,
      };
      if (form.scheduleType === "once") {
        if (!form.scheduledAt) return;
        // Append timezone offset so the backend receives the user's
        // intended local datetime, not a UTC-shifted one.
        const local = new Date(form.scheduledAt);
        const offset = -local.getTimezoneOffset();
        const sign = offset >= 0 ? "+" : "-";
        const hh = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
        const mm = String(Math.abs(offset) % 60).padStart(2, "0");
        input.scheduled_at = `${form.scheduledAt}:00${sign}${hh}:${mm}`;
      } else {
        input.rrule = form.rrule;
        input.rrule_time = form.rruleTime;
        if (form.rrule === "weekly" || form.rrule === "biweekly") {
          input.rrule_day = form.rruleDay;
        }
        if (form.rrule === "monthly") {
          input.rrule_day = form.rruleDay || 1;
        }
      }
      const updated = await updateTask(projectId, task.id, { schedule: input });
      refreshTask(updated);
      setShowForm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [form, projectId, task.id, refreshTask, t]);

  const handleRemove = useCallback(async () => {
    setSaving(true);
    try {
      const updated = await updateTask(projectId, task.id, { schedule: null });
      refreshTask(updated);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  }, [projectId, task.id, refreshTask]);

  const handleTogglePause = useCallback(async () => {
    if (!schedule) return;
    setSaving(true);
    try {
      if (schedule.is_active) {
        await pauseSchedule(projectId, task.id);
      } else {
        await resumeSchedule(projectId, task.id);
      }
      useTasksStore.getState().triggerRefetch();
    } finally {
      setSaving(false);
    }
  }, [schedule, projectId, task.id]);

  // These hooks must be before any conditional returns
  const currentRepeatOption = useMemo(() => {
    if (form.scheduleType !== "recurring") return null;
    const rrule = form.rrule as RepeatOption["rrule"];
    switch (rrule) {
      case "daily":
      case "weekdays":
        return { rrule };
      case "weekly":
      case "biweekly":
        return { rrule, rruleDay: form.rruleDay };
      case "monthly":
        return { rrule, rruleDay: form.rruleDay || 1 };
      default:
        return null;
    }
  }, [form.scheduleType, form.rrule, form.rruleDay]);

  const handleRepeatChange = useCallback((opt: RepeatOption | null) => {
    if (!opt) {
      setForm((f) => ({
        ...f,
        scheduleType: "once",
        rrule: "daily" as Recurrence,
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      scheduleType: "recurring",
      rrule: opt.rrule as Recurrence,
      rruleDay: opt.rruleDay ?? f.rruleDay,
    }));
  }, []);

  const handleDateChange = useCallback(
    (val: string) => {
      const time = val.includes("T")
        ? val.split("T")[1]?.slice(0, 5) || "09:00"
        : form.rruleTime;
      setForm((f) => ({
        ...f,
        scheduledAt: val,
        scheduleType: f.scheduleType === "recurring" ? "recurring" : "once",
        rruleTime: time,
      }));
    },
    [form.rruleTime],
  );

  const handleDateClear = useCallback(() => {
    if (schedule) {
      void handleRemove();
    } else {
      setShowForm(false);
    }
  }, [schedule, handleRemove]);

  /* ---- Popover positioning ---- */
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [posReady, setPosReady] = useState(false);

  // Re-placed whenever the picker changes view (time, repeat): a position
  // measured once leaves a taller view running off the bottom of the window.
  useEffect(() => {
    if (!showForm) {
      setPosReady(false);
      return;
    }
    const popoverEl = popoverRef.current;
    const scrollEl = scrollRef.current;
    const content = scrollEl?.firstElementChild;
    if (!popoverEl || !scrollEl || !content) return;
    const surface: Element = content;

    function place() {
      if (!anchorRef.current || !popoverEl || !scrollEl) return;
      const anchor = anchorRef.current.getBoundingClientRect();
      const box = popoverEl.getBoundingClientRect();
      // Natural height, even when an earlier maxHeight has capped the scroller.
      const height = box.height - scrollEl.clientHeight + scrollEl.scrollHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 8;
      const gap = 4;

      const spaceBelow = vh - anchor.bottom - gap - margin;
      const spaceAbove = anchor.top - gap - margin;

      let top: number;
      let maxHeight: number;

      if (spaceBelow >= height) {
        top = anchor.bottom + gap;
        maxHeight = spaceBelow;
      } else if (spaceAbove >= height) {
        top = anchor.top - gap - height;
        maxHeight = spaceAbove;
      } else if (spaceBelow >= spaceAbove) {
        top = anchor.bottom + gap;
        maxHeight = spaceBelow;
      } else {
        top = margin;
        maxHeight = spaceAbove;
      }

      // Horizontal: anchor right edge to button, clamp
      let left = anchor.right - box.width;
      if (left < margin) left = margin;
      if (left + box.width > vw - margin) left = vw - box.width - margin;

      // Like Radix's popper wrapper: the menu surface's z (index.css, the
      // single z source for menus) goes on this fixed wrapper, whose own
      // stacking context would otherwise trap the surfaces at level 0.
      const zIndex = Number(getComputedStyle(surface).zIndex);
      setPopoverStyle({
        top,
        left,
        maxHeight,
        zIndex: Number.isNaN(zIndex) ? undefined : zIndex,
      });
      setPosReady(true);
    }

    const observer = new ResizeObserver(place);
    observer.observe(content);
    observer.observe(popoverEl);
    window.addEventListener("resize", place);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [showForm]);

  // Close on outside click
  useEffect(() => {
    if (!showForm) return;
    function onMouseDown(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setShowForm(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showForm]);

  const isSaveDisabled =
    saving ||
    (form.scheduleType === "once" && !form.scheduledAt && !currentRepeatOption);

  const popover = showForm
    ? createPortal(
        <div
          ref={popoverRef}
          // No z utility here: place() copies the menu surface's z onto it.
          className="fixed flex flex-col"
          style={{
            ...popoverStyle,
            visibility: posReady ? "visible" : "hidden",
          }}
          data-interactive="true"
          data-testid="task-schedule-form"
          data-task-pocket-owned=""
        >
          <div
            ref={scrollRef}
            className="scrollbar-none min-h-0 flex-1 overflow-y-auto"
          >
            <DatePickerDropdown
              value={form.scheduledAt}
              onChange={handleDateChange}
              onClear={handleDateClear}
              repeat={currentRepeatOption}
              onRepeatChange={handleRepeatChange}
            />
          </div>
          {error && (
            <p className="mt-1 px-2 text-appkit-mini text-danger">{error}</p>
          )}
          <div
            className={cn(
              surfaceRoleClassName("menu"),
              "mt-1 flex shrink-0 gap-1 p-2",
            )}
            {...surfaceRoleAttributes("menu", {
              nativeLevel: "root",
              nativeGroup: "menus",
            })}
            data-modal-menu={insideModal ? "true" : undefined}
          >
            <Button
              size="sm"
              onClick={() => void handleSave()}
              disabled={isSaveDisabled}
              data-testid="task-schedule-save"
            >
              {saving ? t("saving") : tc("save")}
            </Button>
            {schedule && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void handleRemove()}
                disabled={saving}
                className="text-danger hover:bg-danger/10"
                data-testid="task-schedule-remove"
              >
                {tc("remove")}
              </Button>
            )}
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
              data-testid="task-schedule-cancel"
            >
              {tc("cancel")}
            </Button>
          </div>
        </div>,
        document.body,
      )
    : null;

  /* ---- No schedule: one row opens the form ---- */
  if (!schedule) {
    return (
      <div ref={anchorRef}>
        <PocketRow
          testId="task-schedule-add"
          leading={
            <Icons.CalendarClock
              className="h-4 w-4 text-muted"
              aria-hidden="true"
            />
          }
          title={t("schedule")}
          trailing={
            <span className="px-2 text-appkit-body text-muted">{t("add")}</span>
          }
          onSelect={handleOpenForm}
        />
        {popover}
      </div>
    );
  }

  /* ---- Has a schedule: the row edits it, the button pauses or resumes ---- */
  const secondLine = [
    schedule.is_active ? null : t("paused"),
    schedule.next_run_at
      ? t("nextRun", {
          when: formatNextRun(schedule.next_run_at, schedule.timezone),
        })
      : null,
    schedule.run_count > 0 ? t("runs", { count: schedule.run_count }) : null,
    schedule.last_run_at
      ? t("lastRun", {
          when: formatNextRun(schedule.last_run_at, schedule.timezone),
        })
      : null,
  ]
    .filter((part): part is string => part !== null)
    .join(", ");

  return (
    <div ref={anchorRef} data-testid="task-schedule-display">
      <PocketRow
        testId="task-schedule-edit"
        leading={
          <Icons.CalendarClock
            className="h-4 w-4 text-muted"
            aria-hidden="true"
          />
        }
        title={scheduleLabel(schedule)}
        secondLine={secondLine || undefined}
        trailing={
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            onClick={() => void handleTogglePause()}
            disabled={saving}
            aria-label={
              schedule.is_active ? t("pauseSchedule") : t("resumeSchedule")
            }
            data-testid="task-schedule-toggle"
          >
            {schedule.is_active ? (
              <Icons.Pause className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Icons.Play className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        }
        onSelect={handleOpenForm}
      />
      {popover}
    </div>
  );
}
